import React, { createContext, useContext, useState, useEffect } from 'react';
import { useWallet } from './WalletContext';

export type VerificationLevel = 'unverified' | 'email' | 'kyc' | 'whitelist' | 'admin';
export type VerificationStatus = 'pending' | 'approved' | 'rejected' | 'expired';

interface VerificationData {
  level: VerificationLevel;
  status: VerificationStatus;
  emailVerified: boolean;
  kycCompleted: boolean;
  whitelistStatus: VerificationStatus;
  adminVerified: boolean;
  lastVerified: Date | null;
  verificationExpiry: Date | null;
}

interface VerificationContextType {
  verification: VerificationData;
  updateVerification: (updates: Partial<VerificationData>) => void;
  canVote: boolean;
  canAccessAdmin: boolean;
  canCreateProposals: boolean;
  canAccessAnalytics: boolean;
  canAccessSettings: boolean;
  getVerificationMessage: () => string;
  getRequiredVerification: (feature: string) => VerificationLevel;
}

const VerificationContext = createContext<VerificationContextType | undefined>(undefined);

export const useVerification = () => {
  const context = useContext(VerificationContext);
  if (context === undefined) {
    throw new Error('useVerification must be used within a VerificationProvider');
  }
  return context;
};

export const VerificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isConnected, address } = useWallet();
  
  const [verification, setVerification] = useState<VerificationData>({
    level: 'unverified',
    status: 'pending',
    emailVerified: false,
    kycCompleted: false,
    whitelistStatus: 'pending',
    adminVerified: false,
    lastVerified: null,
    verificationExpiry: null
  });

  const updateVerification = (updates: Partial<VerificationData>) => {
    setVerification(prev => ({ ...prev, ...updates }));
  };

  // Feature access control based on verification level
  const canVote = verification.level === 'kyc' || verification.level === 'whitelist' || verification.level === 'admin';
  const canAccessAdmin = verification.level === 'admin';
  const canCreateProposals = verification.level === 'whitelist' || verification.level === 'admin';
  const canAccessAnalytics = verification.level === 'whitelist' || verification.level === 'admin';
  const canAccessSettings = verification.level !== 'unverified';

  const getVerificationMessage = () => {
    switch (verification.level) {
      case 'unverified':
        return 'Please verify your email to access basic features';
      case 'email':
        return 'Complete KYC verification to access voting and governance features';
      case 'kyc':
        return 'Apply for whitelist status to create proposals and access advanced analytics';
      case 'whitelist':
        return 'You have full access to all syndicate features';
      case 'admin':
        return 'You have administrative access to all system functions';
      default:
        return 'Verification status unknown';
    }
  };

  const getRequiredVerification = (feature: string): VerificationLevel => {
    const requirements: Record<string, VerificationLevel> = {
      'voting': 'kyc',
      'governance': 'kyc',
      'create-proposals': 'whitelist',
      'analytics': 'whitelist',
      'admin': 'admin',
      'settings': 'email',
      'treasury': 'email',
      'horses': 'email',
      'dashboard': 'email'
    };
    return requirements[feature] || 'unverified';
  };

  // Auto-verify authenticated users
  useEffect(() => {
    if (isConnected && address) {
      // Simulate different verification levels based on user
      const userLevel = address.includes('1234') ? 'kyc' : 'email'; // Mock logic
      
      setVerification(prev => ({
        ...prev,
        level: userLevel,
        status: 'approved',
        emailVerified: true,
        kycCompleted: userLevel === 'kyc' || userLevel === 'whitelist' || userLevel === 'admin',
        whitelistStatus: userLevel === 'whitelist' || userLevel === 'admin' ? 'approved' : 'pending',
        adminVerified: userLevel === 'admin',
        lastVerified: new Date(),
        verificationExpiry: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000) // 1 year
      }));
    } else {
      // Reset verification when disconnected
      setVerification({
        level: 'unverified',
        status: 'pending',
        emailVerified: false,
        kycCompleted: false,
        whitelistStatus: 'pending',
        adminVerified: false,
        lastVerified: null,
        verificationExpiry: null
      });
    }
  }, [isConnected, address]);

  // Simulate verification progression for unauthenticated users
  useEffect(() => {
    if (!isConnected) {
      const timer = setTimeout(() => {
        setVerification(prev => ({
          ...prev,
          level: 'email',
          emailVerified: true,
          status: 'approved',
          lastVerified: new Date()
        }));
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isConnected]);

  return (
    <VerificationContext.Provider value={{
      verification,
      updateVerification,
      canVote,
      canAccessAdmin,
      canCreateProposals,
      canAccessAnalytics,
      canAccessSettings,
      getVerificationMessage,
      getRequiredVerification
    }}>
      {children}
    </VerificationContext.Provider>
  );
};
